import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from 'react';
import { FeaturedJob, FeaturedMedia } from 'rwl-shared/dist/models';
import { useContentLists } from '../ContentProvider';
import { useFetchJob } from './useFetchjob';

export interface JobContextProps {
    job: FeaturedJob | undefined;
    setJobId: (jobId: string | undefined) => void;
    clearJob: () => void;
    selectedMedia: FeaturedMedia | undefined;
    setSelectedMedia: (selectedMedia: FeaturedMedia | undefined) => void;
    isLoading: boolean;
}

const JobContext = createContext<JobContextProps | undefined>(undefined);

export const JobProvider = ({ children }: { children: React.ReactNode }) => {
    const { jobList, similarJobList } = useContentLists();
    const [jobId, setJobId] = useState<string | undefined>();
    const [job, setJob] = useState<FeaturedJob | undefined>();
    const [selectedMedia, setSelectedMedia] = useState<
        FeaturedMedia | undefined
    >();
    const [isLoading, setIsLoading] = useState(false);

    const { data: fetchedJob, isLoading: fetchLoading } = useFetchJob({
        jobId,
        enabled: !job,
    });

    const clearJob = useCallback(() => {
        setJobId(undefined);
        setJob(undefined);
    }, []);

    const findExistingJob = (jobId: string | undefined) =>
        [...jobList, ...similarJobList].find((job) => job.id === jobId);

    useEffect(() => {
        const existingJob = findExistingJob(jobId);
        if (existingJob) {
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
    }, [jobId, jobList]);

    useEffect(() => {
        setJob(fetchedJob);
        setIsLoading(false);
    }, [fetchedJob]);

    return (
        <JobContext.Provider
            value={{
                job,
                setJobId: (jobId: string | undefined) => {
                    clearJob();
                    const existingJob = findExistingJob(jobId);
                    if (existingJob) {
                        setJob(existingJob);
                    }
                    setJobId(jobId);
                },
                clearJob,
                isLoading: fetchLoading || isLoading,
                selectedMedia,
                setSelectedMedia,
            }}
        >
            {children}
        </JobContext.Provider>
    );
};

export const useJob = () => {
    const context = useContext(JobContext);
    if (!context) {
        throw new Error('useJob must be used within a JobProvider');
    }
    return context;
};
